import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Play,
  CheckCircle,
  AlertTriangle,
  Clock,
  Camera,
  Flag,
  Wrench,
  Shield,
  Loader2,
  Save,
  Edit2,
  Sparkles,
  Trash2,
  ChevronRight,
} from "lucide-react";
import { sopsAPI, maintenanceAPI } from "../api";
import { useAuthStore } from "../store/authStore";
import clsx from "clsx";
import toast from "react-hot-toast";

const SOPViewer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  // State Management
  const [isExecuting, setIsExecuting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isUploading, setIsUploading] = useState(false); // New state for camera
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState({});
  const [notes, setNotes] = useState({});
  const [finalSummary, setFinalSummary] = useState("");
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [editData, setEditData] = useState(null);

  const { data: sop, isLoading } = useQuery({
    queryKey: ["sop", id],
    queryFn: () => sopsAPI.getById(id).then((res) => res.data.data),
  });
  console.log("fetched user:", user);
  const userRole = user?.role?.toLowerCase() || "";
  console.log("User Role:", userRole);
  const isAdmin = userRole === "super_admin" || userRole === "admin";
  const sopStatus = sop?.status?.toLowerCase() || "";
  const isDraft = sopStatus === "draft";

  useEffect(() => {
    if (sop && !editData) {
      setEditData({
        category: sop.category || "Operation",
        estimated_duration: sop.estimated_duration || 30,
        skill_level: sop.skill_level || "Intermediate",
      });
    }
  }, [sop]);

  // Mutations
  const approveMutation = useMutation({
    mutationFn: () => sopsAPI.updateStatus(id, "approved"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sop", id] });
      toast.success("SOP has been approved and published");
    },
  });

  const updateMetadataMutation = useMutation({
    mutationFn: (data) => sopsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sop", id] });
      setIsEditing(false);
      toast.success("Metadata updated successfully");
    },
  });

  const logMutation = useMutation({
    mutationFn: (data) => maintenanceAPI.generateAiLog(data),
    onSuccess: (res) => {
      toast.success("Work completed and maintenance log filed.");
      navigate("/maintenance");
    },
    onError: (err) => toast.error("Failed to file log: " + err.message),
  });

  // --- Image Handling Logic ---
  const handleCapture = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("image", file);

    setIsUploading(true);
    try {
      // Upload to server and get the permanent URL
      const res = await maintenanceAPI.uploadImage(formData);
      setCapturedPhoto(res.data.url);
      toast.success("Photo captured and uploaded");
    } catch (err) {
      toast.error("Failed to upload image");
      console.error(err);
    } finally {
      setIsUploading(false);
      e.target.value = ""; // Reset input
    }
  };

  const removePhoto = () => {
    setCapturedPhoto(null);
    toast.success("Photo removed");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (!sop) {
    return (
      <div className="text-center py-12 text-slate-800">SOP not found.</div>
    );
  }

  const steps = sop.steps || [];
  const progress =
    steps.length > 0
      ? Math.round((Object.keys(completedSteps).length / steps.length) * 100)
      : 0;

  const handleCompleteStep = (stepIndex) => {
    const step = steps[stepIndex];
    if (step.requiresImage && !capturedPhoto) {
      toast.error("A photo is required for this step.");
      return;
    }
    // Commit current step data to local state
    setCompletedSteps((prev) => ({
      ...prev,
      [stepIndex]: {
        completedAt: new Date(),
        photoUrl: capturedPhoto, // Saving the real URL
        notes: notes[stepIndex] || "",
      },
    }));
    setCapturedPhoto(null);
    if (stepIndex < steps.length - 1) {
      setCurrentStep(stepIndex + 1);
    } else {
      setIsFinalizing(true);
    }
  };

  const handleFinishAndFile = () => {
    if (!finalSummary.trim()) {
      toast.error("Please provide a final summary of the work done.");
      return;
    }

    const executionDataArray = steps.map((s, i) => ({
      stepIndex: i,
      instruction: s.instruction,
      notes: completedSteps[i]?.notes || notes[i] || "",
      photoUrl: completedSteps[i]?.photoUrl || null,
      completedAt: completedSteps[i]?.completedAt || new Date(),
    }));

    logMutation.mutate({
      logId: null,
      rawNotes: finalSummary,
      machine_id: sop.machine_id,
      sop_id: id,
      executionData: executionDataArray,
    });
  };

  const Badge = ({ children, type = "primary", className = "" }) => {
    // Reverting to text-xs
    let classes = "px-3 py-1 text-xs font-semibold rounded-full";
    if (type === "primary") classes += " bg-blue-100 text-blue-800";
    if (type === "warning") classes += " bg-yellow-100 text-yellow-800";
    if (type === "danger") classes += " bg-red-100 text-red-800";
    if (type === "success") classes += " bg-green-100 text-green-800";
    if (type === "neutral") classes += " bg-slate-100 text-slate-800";
    return <span className={clsx(classes, className)}>{children}</span>;
  };

  const Button = ({
    children,
    variant = "primary",
    className = "",
    ...props
  }) => {
    let baseClasses =
      // Retaining enhanced padding/shadows, keeping text-sm
      "btn px-5 py-2.5 rounded-xl font-semibold transition-colors duration-200 flex items-center justify-center text-sm";
    if (variant === "primary")
      baseClasses += " bg-blue-600 text-white hover:bg-blue-700 shadow-lg";
    if (variant === "secondary")
      baseClasses += " bg-slate-100 text-slate-700 hover:bg-slate-200";
    if (variant === "success")
      baseClasses += " bg-green-500 text-white hover:bg-green-600 shadow-lg";
    if (variant === "ghost")
      baseClasses += " bg-transparent text-slate-500 hover:bg-slate-100";
    if (props.disabled) baseClasses += " opacity-60 cursor-not-allowed";

    return (
      <button className={clsx(baseClasses, className)} {...props}>
        {children}
      </button>
    );
  };

  const Card = ({ children, className = "", borderTop = false }) => (
    <div
      className={clsx(
        // Retaining larger padding and stronger shadow
        "bg-white border border-slate-200 p-7 rounded-2xl shadow-xl",
        className,
        borderTop && "border-t-4 border-t-blue-500",
      )}
    >
      {children}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-10 relative pb-16">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
        <div className="flex items-center gap-6">
          <Link to="/sops">
            <Button variant="ghost" className="p-3">
              <ArrowLeft className="w-6 h-6" />
            </Button>
          </Link>
          <div className="flex-1">
            {/* Reverting to text-2xl */}
            <h1 className="text-2xl font-bold text-slate-900">{sop.title}</h1>
            <p className="text-slate-500 mt-1">
              {sop.machine_name || "General SOP"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 self-end sm:self-center flex-shrink-0">
          {isAdmin && !isExecuting && (
            <Button
              onClick={() => setIsEditing(!isEditing)}
              variant="secondary"
              className="gap-2" // Removing explicit text-size here, relies on component default (text-sm)
            >
              <Edit2 className="w-4 h-4" />
              {isEditing ? "Cancel Edit" : "Edit Metadata"}
            </Button>
          )}
          {isAdmin && isDraft && !isExecuting && (
            <Button
              onClick={() => approveMutation.mutate()}
              variant="success"
              className="gap-2" // Removing explicit text-size here
              disabled={approveMutation.isPending}
            >
              {approveMutation.isPending ? (
                <Loader2 className="animate-spin w-4 h-4" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              Approve & Publish
            </Button>
          )}
          {!isExecuting && sopStatus === "approved" && (
            <Button
              onClick={() => setIsExecuting(true)}
              variant="primary"
              className="gap-2" // Removing explicit text-size here
            >
              <Play className="w-4 h-4" />
              Start Procedure
            </Button>
          )}
        </div>
      </div>

      {isExecuting ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Progress Sidebar */}
          <Card className="p-6 h-fit lg:sticky top-6">
            <h3 className="font-bold text-slate-900 mb-6 text-lg">
              Execution Progress
            </h3>
            <div className="flex justify-between items-center mb-3">
              <span className="text-sm font-bold text-blue-600">
                {progress}% Complete
              </span>
              <span className="text-sm text-slate-500">
                {Object.keys(completedSteps).length}/{steps.length} Steps
              </span>
            </div>
            <div className="w-full h-3 bg-slate-200 rounded-full mb-8">
              <div
                className="h-full bg-blue-600 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="space-y-2">
              {steps.map((s, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentStep(i)}
                  className={clsx(
                    // Reverting to text-sm
                    "w-full text-left p-3 rounded-xl text-sm flex gap-3 items-center transition-all duration-200 border-2",
                    currentStep === i
                      ? "bg-blue-50 border-blue-500 text-blue-800 font-bold shadow-md"
                      : completedSteps[i]
                        ? "bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
                        : "bg-white border-slate-200 text-slate-700 hover:bg-slate-100",
                  )}
                >
                  <div
                    className={clsx(
                      "w-7 h-7 rounded-full flex items-center justify-center border-2 flex-shrink-0 font-extrabold text-xs", // Reverted to text-xs
                      completedSteps[i]
                        ? "bg-green-600 border-green-600 text-white shadow-inner"
                        : currentStep === i
                          ? "bg-blue-600 border-blue-600 text-white shadow-inner"
                          : "border-slate-300 text-slate-600",
                    )}
                  >
                    {completedSteps[i] ? "✓" : i + 1}
                  </div>
                  <span className="truncate flex-1">{s.instruction}</span>
                  {completedSteps[i] && (
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          </Card>

          {/* Main Execution Area */}
          <div className="lg:col-span-2">
            <AnimatePresence mode="wait">
              {!isFinalizing ? (
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.3 }}
                  className="card bg-white border border-slate-200 p-8 rounded-3xl shadow-2xl" // Reduced padding slightly from 10 to 8
                >
                  <div className="flex justify-between items-center mb-6 border-b pb-4">
                    <Badge type="primary" className="py-1 px-3 text-sm">
                      {" "}
                      {/* Adjusted badge size */}
                      Step {currentStep + 1} of {steps.length}
                    </Badge>
                    <div className="flex gap-2">
                      {steps[currentStep]?.checkPoint && (
                        <Badge
                          type="warning"
                          className="flex items-center gap-1"
                        >
                          <Flag className="w-3 h-3" /> Checkpoint
                        </Badge>
                      )}
                      {steps[currentStep]?.requiresImage && (
                        <Badge
                          type="danger"
                          className="flex items-center gap-1"
                        >
                          <Camera className="w-3 h-3" /> Photo Required
                        </Badge>
                      )}
                    </div>
                  </div>
                  <h2 className="text-2xl font-extrabold text-slate-900 mb-8 min-h-[64px]">
                    {steps[currentStep]?.instruction}
                  </h2>

                  {steps[currentStep]?.warning && (
                    <div className="p-4 bg-red-50 border border-red-400 rounded-xl mb-6 flex gap-3 shadow-inner">
                      {" "}
                      {/* Reduced padding slightly from 5 to 4 */}
                      <AlertTriangle className="text-red-700 w-5 h-5 flex-shrink-0" />
                      <p className="text-red-900 text-sm font-semibold">
                        {" "}
                        {/* Reverted to text-sm */}
                        SAFETY WARNING: {steps[currentStep].warning}
                      </p>
                    </div>
                  )}

                  <div className="space-y-2 mb-8">
                    <label className="text-sm font-bold text-slate-800">
                      Observation Notes (Optional)
                    </label>
                    <textarea
                      className="w-full p-3 border border-slate-300 rounded-xl focus:ring-blue-500 focus:border-blue-500 min-h-[100px] text-sm resize-none shadow-sm"
                      placeholder="Add observations, issues, or details for this step..."
                      value={notes[currentStep] || ""}
                      onChange={(e) =>
                        setNotes({ ...notes, [currentStep]: e.target.value })
                      }
                    />
                  </div>

                  {/* CAMERA UI SECTION */}
                  <div className="p-5 bg-slate-50 rounded-2xl mb-8 border border-slate-200">
                    <h4 className="font-bold text-slate-700 mb-4 flex items-center gap-2 text-base">
                      {" "}
                      {/* Reverted to text-base */}
                      <Camera className="w-5 h-5 text-blue-600" /> Photo
                      Documentation
                    </h4>

                    {!capturedPhoto ? (
                      <div className="flex-1">
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          id="sop-camera-input"
                          className="hidden"
                          onChange={handleCapture}
                          disabled={isUploading}
                        />
                        <label
                          htmlFor="sop-camera-input"
                          className={clsx(
                            "w-full py-3 cursor-pointer flex items-center justify-center rounded-xl font-semibold text-sm transition-all duration-200", // Reverted to text-sm
                            isUploading
                              ? "opacity-60 bg-slate-200 text-slate-500"
                              : "bg-blue-100 text-blue-700 hover:bg-blue-200 shadow-sm",
                          )}
                        >
                          {isUploading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Camera className="w-4 h-4 mr-2" />
                          )}
                          {isUploading
                            ? "Uploading..."
                            : "Capture/Attach Photo"}
                        </label>
                      </div>
                    ) : (
                      <div className="flex items-center gap-4 bg-white border border-green-300 p-3 rounded-xl shadow-md">
                        <img
                          src={capturedPhoto}
                          alt="Captured"
                          className="w-16 h-16 object-cover rounded-lg border border-green-200"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-bold text-green-700">
                            {" "}
                            {/* Reverted to text-sm */}
                            Photo Attached Successfully
                          </p>
                          <p className="text-xs text-green-600">
                            Required:{" "}
                            {steps[currentStep]?.requiresImage ? "Yes" : "No"}
                          </p>
                        </div>
                        <Button
                          onClick={removePhoto}
                          variant="ghost"
                          className="p-2 text-red-500 hover:bg-red-50"
                        >
                          <Trash2 className="w-5 h-5" />
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Navigation Buttons */}
                  <div className="grid grid-cols-2 gap-4 pt-4">
                    <Button
                      onClick={() =>
                        setCurrentStep(Math.max(0, currentStep - 1))
                      }
                      disabled={currentStep === 0}
                      variant="secondary"
                    >
                      <ArrowLeft className="w-4 h-4" /> Previous Step
                    </Button>
                    <Button
                      onClick={() => handleCompleteStep(currentStep)}
                      variant="primary"
                      disabled={
                        isUploading ||
                        (steps[currentStep]?.requiresImage && !capturedPhoto)
                      }
                    >
                      {currentStep === steps.length - 1
                        ? "Finish Procedure"
                        : "Complete & Next"}
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className="card bg-white border-t-8 border-t-blue-600 p-8 rounded-2xl shadow-2xl" // Reduced padding slightly
                >
                  <h2 className="text-2xl font-extrabold text-slate-900 mb-3">
                    {" "}
                    {/* Reverted to text-2xl */}
                    Finalize Work Log
                  </h2>
                  <p className="text-slate-500 mb-6">
                    {" "}
                    {/* Reverted to default text size */}
                    Provide a comprehensive summary of the entire work
                    performed.
                  </p>
                  <label className="text-sm font-bold text-slate-800 mb-2 block">
                    {" "}
                    {/* Reverted to text-sm */}
                    Final Summary
                  </label>
                  <textarea
                    className="w-full p-4 border border-slate-300 rounded-xl focus:ring-blue-500 focus:border-blue-500 min-h-[200px] mb-8 text-sm resize-none shadow-inner" // Reverted to text-sm and smaller min-height
                    placeholder="e.g., Successfully replaced the main filtration unit..."
                    value={finalSummary}
                    onChange={(e) => setFinalSummary(e.target.value)}
                  />
                  <div className="flex gap-4">
                    <Button
                      onClick={() => setIsFinalizing(false)}
                      variant="secondary"
                    >
                      Review Steps
                    </Button>
                    <Button
                      onClick={handleFinishAndFile}
                      variant="primary"
                      className="flex-1 gap-2"
                      disabled={logMutation.isPending || !finalSummary.trim()}
                    >
                      {logMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" /> File Log with AI
                        </>
                      )}
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Procedure Steps */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <h3 className="text-xl font-bold text-slate-800 mb-5 border-b border-slate-100 pb-3">
                Procedure Steps
              </h3>
              <div className="space-y-4">
                {steps.map((s, i) => (
                  <div
                    key={i}
                    className="flex gap-4 p-4 bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200" // Reduced padding
                  >
                    <div className="w-9 h-9 rounded-full bg-blue-600 text-white font-bold flex-shrink-0 flex items-center justify-center text-base shadow-md">
                      {" "}
                      {/* Reduced size slightly */}
                      {i + 1}
                    </div>
                    <div className="flex-1 pt-0.5">
                      <p className="text-slate-800 font-medium">
                        {" "}
                        {/* Reverted to default size */}
                        {s.instruction}
                      </p>
                      {s.warning && (
                        <div className="mt-2 p-2 bg-red-50 text-red-700 text-xs rounded-lg flex gap-2 items-start border border-red-200">
                          {" "}
                          {/* Reverted to text-xs */}
                          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                          <span className="font-medium">
                            WARNING: {s.warning}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-3 items-center pt-0.5 flex-shrink-0">
                      {s.requiresImage && (
                        <Camera
                          className="w-4 h-4 text-red-500"
                          title="Image required"
                        />
                      )}
                      {s.checkPoint && (
                        <Flag
                          className="w-4 h-4 text-yellow-500"
                          title="Checkpoint"
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Information Sidebar */}
          <div className="space-y-6 lg:sticky top-6 h-fit">
            <Card>
              <div className="flex justify-between items-center mb-6 border-b border-slate-100 pb-4">
                <h3 className="text-xl font-bold text-slate-800">Metadata</h3>
                <Badge
                  type={sopStatus === "approved" ? "success" : "warning"}
                  className="py-1.5 px-3 text-xs" // Reverted to text-xs/sm mix
                >
                  {sop.status}
                </Badge>
              </div>
              <div className="space-y-5">
                {isEditing ? (
                  <div className="space-y-4">
                    {/* Edit form */}
                    {["category", "estimated_duration", "skill_level"].map(
                      (field) => (
                        <div key={field}>
                          <label className="label text-xs font-semibold text-slate-700 block mb-1 capitalize">
                            {field.replace("_", " ")}
                          </label>
                          {field === "category" || field === "skill_level" ? (
                            <select
                              className="w-full p-2 border border-slate-300 rounded-lg text-sm shadow-sm" // Reverted to text-sm
                              value={editData[field]}
                              onChange={(e) =>
                                setEditData({
                                  ...editData,
                                  [field]: e.target.value,
                                })
                              }
                            >
                              {field === "category" && (
                                <>
                                  <option>Operation</option>
                                  <option>Maintenance</option>
                                  <option>Troubleshooting</option>
                                </>
                              )}
                              {field === "skill_level" && (
                                <>
                                  <option>Basic</option>
                                  <option>Intermediate</option>
                                  <option>Advanced</option>
                                </>
                              )}
                            </select>
                          ) : (
                            <input
                              type="number"
                              className="w-full p-2 border border-slate-300 rounded-lg text-sm shadow-sm" // Reverted to text-sm
                              value={editData[field]}
                              onChange={(e) =>
                                setEditData({
                                  ...editData,
                                  [field]: e.target.value,
                                })
                              }
                            />
                          )}
                        </div>
                      ),
                    )}
                    <Button
                      onClick={() => updateMetadataMutation.mutate(editData)}
                      variant="primary"
                      className="w-full gap-2 mt-4"
                      disabled={updateMetadataMutation.isPending}
                    >
                      <Save className="w-4 h-4" /> Save Changes
                    </Button>
                  </div>
                ) : (
                  <ul className="space-y-4 text-sm">
                    {" "}
                    {/* Reverted to text-sm */}
                    {[
                      { label: "Category", value: sop.category },
                      {
                        label: "Est. Duration",
                        value: `${sop.estimated_duration} min`,
                        icon: Clock,
                      },
                      { label: "Skill Level", value: sop.skill_level },
                      {
                        label: "Version",
                        value: sop.version || "1.0",
                        border: true,
                      },
                    ].map((item, i) => (
                      <li
                        key={i}
                        className={clsx(
                          "flex justify-between items-center py-1", // Reduced vertical padding
                          item.border && "pt-4 border-t border-slate-100 mt-2",
                        )}
                      >
                        <span className="text-slate-500 font-medium flex items-center gap-2">
                          {item.icon && (
                            <item.icon className="w-4 h-4 text-blue-500" />
                          )}
                          {item.label}
                        </span>
                        <span className="font-bold text-slate-800">
                          {item.value}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Card>

            <Card>
              <h3 className="text-xl font-bold text-slate-800 mb-5 border-b border-slate-100 pb-3">
                Safety & Tools
              </h3>
              <div className="space-y-6">
                <div>
                  <p className="label text-sm font-bold text-slate-700 mb-3">
                    Safety Warnings
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {sop.safety_warnings?.length > 0 ? (
                      sop.safety_warnings.map((w, i) => (
                        <Badge
                          key={i}
                          type="danger"
                          className="flex items-center gap-1.5 py-1.5 px-3 shadow-sm"
                        >
                          <Shield className="w-6 h-4" />
                          {w}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-sm text-slate-400 italic">
                        No specific warnings defined.
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <p className="label text-sm font-bold text-slate-700 mb-3">
                    Required Tools
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {sop.required_tools?.length > 0 ? (
                      sop.required_tools.map((t, i) => (
                        <Badge
                          key={i}
                          type="neutral"
                          className="flex items-center gap-1.5 py-1.5 px-3 shadow-sm"
                        >
                          <Wrench className="w-4 h-4" />
                          {t}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-sm text-slate-400 italic">
                        No specific tools listed.
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default SOPViewer;
